import React from "react";
import { FaExclamationCircle } from "react-icons/fa";
import "./PayslipTemplate.css";

const PayslipTemplate = ({
  employee,
  salary,
  showActions = true,
  editableEarnings = {},
  editableDeductions = {},
  isEditing = false,
  handleEarningsChange = () => {},
  handleDeductionsChange = () => {},
  payslipLabels = {
    payslipTitle: "Payslip For the Month",
    employee: {},
    bank: {},
    earnings: {},
    deductions: {},
  },
  companyInfo = {
    name: "Dremerz Consultancy & Services",
    address:
      "No:2/14, Kaveri Nagar Koladi Road, Thiruverkadu, Chennai -600077 India",
    logo: "",
  },
  getEmployeeFieldValue,
  getBankFieldValue,
  getDynamicEarningsValue,
  getDynamicDeductionsValue,
  getCurrentEarningsValue,
  getCurrentDeductionsValue,
  dynamicEarnings = [],
  dynamicDeductions = [],
  numberToWords,
  checkMissingEmployeeDetails,
}) => {
  const formatPayDate = (dateString) => {
    if (!dateString) return "Not Generated";
    try {
      return new Date(dateString)
        .toLocaleDateString("en-GB")
        .split("/")
        .join("-");
    } catch (error) {
      console.error("Error formatting pay date:", error);
      return "Invalid Date";
    }
  };

  const formatPeriodDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString)
        .toLocaleDateString("en-GB")
        .split("/")
        .join("-");
    } catch {
      return "Invalid Date";
    }
  };

  if (!employee || !salary)
    return (
      <div className="text-center text-muted p-3 p-md-5">
        <h4 className="fs-5 fs-md-4">
          No payslip data available for selected employee
        </h4>
        <p className="fs-6 fs-md-5">
          Please ensure the employee has salary records for the selected period
        </p>
      </div>
    );

  const missingDetails = checkMissingEmployeeDetails
    ? checkMissingEmployeeDetails(employee)
    : [];

  if (missingDetails.length > 0) {
    return (
      <div className="text-center p-3 p-md-5 border border-danger rounded bg-light">
        <div className="text-danger mb-3">
          <FaExclamationCircle size={32} className="d-md-none" />
          <FaExclamationCircle size={48} className="d-none d-md-block" />
        </div>
        <h4 className="text-danger fs-5 fs-md-4">Cannot Generate Payslip</h4>
        <div className="text-start mt-3 mt-md-4">
          <h6 className="text-danger fs-6">Missing Details:</h6>
          <ul className="list-unstyled text-danger">
            {missingDetails.map((detail, index) => (
              <li key={index} className="mb-1 fs-6">
                {detail}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-muted mt-3 fs-6">
          Please update the employee's profile with the missing information
          before generating the payslip.
        </p>
      </div>
    );
  }

  const getPaidDays = () => {
    if (salary.workingDays !== undefined && salary.workingDays !== null) {
      return salary.workingDays.toString();
    }
    if (!salary.startDate || !salary.endDate) return "N/A";

    try {
      const start = new Date(salary.startDate);
      const end = new Date(salary.endDate);
      const timeDiff = end.getTime() - start.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;

      return daysDiff > 0 ? `${daysDiff}` : "N/A";
    } catch (error) {
      console.error("Error calculating paid days:", error);
      return "N/A";
    }
  };

  const renderCompanyLogo = () => {
    if (companyInfo.logo && companyInfo.logo.includes(".")) {
      return (
        <img
          src={`https://api.dremerz.net/uploads/${companyInfo.logo}`}
          alt="Company Logo"
          className="company-logo"
          onError={(e) => {
            e.target.style.display = "none";
            if (e.target.nextSibling) {
              e.target.nextSibling.style.display = "flex";
            }
          }}
        />
      );
    }
  };

  const getCorrectEarningsValue = (id, labelInfo) => {
    if (getCurrentEarningsValue) {
      return getCurrentEarningsValue(id, labelInfo);
    }

    if (isEditing && editableEarnings && typeof editableEarnings === "object") {
      const value = editableEarnings[id];
      if (value === undefined || value === null) {
        return getDynamicEarningsValue
          ? getDynamicEarningsValue(labelInfo.label)
          : 0;
      }
      return value === ""
        ? 0
        : typeof value === "number"
        ? value
        : parseFloat(value) || 0;
    } else {
      return getDynamicEarningsValue
        ? getDynamicEarningsValue(labelInfo.label)
        : 0;
    }
  };

  const getCorrectDeductionsValue = (id, labelInfo) => {
    if (getCurrentDeductionsValue) {
      return getCurrentDeductionsValue(id, labelInfo);
    }

    if (
      isEditing &&
      editableDeductions &&
      typeof editableDeductions === "object"
    ) {
      const value = editableDeductions[id];
      if (value === undefined || value === null) {
        return getDynamicDeductionsValue
          ? getDynamicDeductionsValue(labelInfo.label)
          : 0;
      }
      return value === ""
        ? 0
        : typeof value === "number"
        ? value
        : parseFloat(value) || 0;
    } else {
      return getDynamicDeductionsValue
        ? getDynamicDeductionsValue(labelInfo.label)
        : 0;
    }
  };

  const totalEarnings = Object.entries(payslipLabels.earnings || {}).reduce(
    (sum, [id, labelInfo]) => {
      const value = getCorrectEarningsValue(id, labelInfo);
      return sum + value;
    },
    0
  );

  const totalDeductions = Object.entries(payslipLabels.deductions || {}).reduce(
    (sum, [id, labelInfo]) => {
      const value = getCorrectDeductionsValue(id, labelInfo);
      return sum + value;
    },
    0
  );

  const netPay = totalEarnings - totalDeductions;

  return (
    <div
      id={`payslip-content-${employee.id}-${salary.startDate}-${salary.endDate}`}
      className="bg-white p-2 p-md-4 shadow-sm"
    >
      {/* Header Section */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 mb-md-4 pb-2 pb-md-3 border-bottom">
        <div className="d-flex align-items-center mb-2 mb-md-0 w-100">
          {renderCompanyLogo()}
          <div className="ms-2 ms-md-3 flex-grow-1">
            <h2 className="mb-0 fs-6 fs-md-5 fw-bold text-start">
              {companyInfo.name}
            </h2>
            <div className="text-muted small text-start">
              {companyInfo.address}
            </div>
          </div>
        </div>
        <div className="text-start text-md-end w-100 w-md-auto">
          <div className="fw-bold fs-6 fs-md-5">
            {payslipLabels.payslipTitle}
          </div>
          <div className="text-muted small">
            ({formatPeriodDate(salary.startDate)}) - (
            {formatPeriodDate(salary.endDate)})
          </div>
        </div>
      </div>

      {/* Employee Summary */}
      <div className="d-flex flex-column flex-md-row justify-content-between mb-3 mb-md-4 pb-2 pb-md-3 border-bottom-dashed">
        <div className="flex-grow-1 mb-3 mb-md-0">
          <div className="fw-bold text-secondary mb-2 text-start">
            EMPLOYEE SUMMARY
          </div>

          {Object.entries(payslipLabels.employee || {}).map(
            ([id, labelInfo]) => (
              <div key={id} className="d-flex mb-1">
                <span className="text-muted me-2 label-min-width text-start">
                  {labelInfo.label}
                </span>
                <span className="me-2">:</span>
                <span className="text-break">
                  {getEmployeeFieldValue
                    ? getEmployeeFieldValue(employee, salary, labelInfo.key)
                    : "N/A"}
                </span>
              </div>
            )
          )}

          <div className="d-flex mb-1">
            <span className="text-muted me-2 label-min-width text-start">
              Pay Date
            </span>
            <span className="me-2">:</span>
            <span className="text-break">
              {salary.createdAt
                ? formatPayDate(salary.createdAt)
                : "Not Generated"}
            </span>
          </div>
        </div>

        <div className="flex-grow-1 ms-md-4 p-2 p-md-3 border rounded bg-light net-pay-section">
          <div className="fw-bold text-success mb-2 text-start fs-5 fs-md-4">
            ₹{netPay.toLocaleString("en-IN")}
          </div>
          <div className="fw-bold text-secondary mb-2 mb-md-3 pb-1 pb-md-2 border-bottom-dashed text-start">
            Total Net Pay
          </div>
          <div className="d-flex mb-1">
            <span className="text-muted me-2 label-min-width text-start">
              Paid Days
            </span>
            <span className="me-2">:</span>
            <span>{getPaidDays()}</span>
          </div>
        </div>
      </div>

      {/* Bank Details */}
      <div className="d-flex flex-column flex-md-row justify-content-between mb-3 mb-md-4 pb-2 pb-md-3 border-bottom-dashed">
        <div className="flex-grow-1 mb-2 mb-md-0">
          {Object.entries(payslipLabels.bank || {})
            .slice(
              0,
              Math.ceil(Object.keys(payslipLabels.bank || {}).length / 2)
            )
            .map(([id, labelInfo]) => (
              <div key={id} className="d-flex mb-1">
                <span className="text-muted me-2 label-min-width text-start">
                  {labelInfo.label}
                </span>
                <span className="me-2">:</span>
                <span className="text-break">
                  {getBankFieldValue
                    ? getBankFieldValue(employee, labelInfo.key)
                    : "N/A"}
                </span>
              </div>
            ))}
        </div>

        <div className="flex-grow-1">
          {Object.entries(payslipLabels.bank || {})
            .slice(Math.ceil(Object.keys(payslipLabels.bank || {}).length / 2))
            .map(([id, labelInfo]) => (
              <div key={id} className="d-flex mb-1">
                <span className="text-muted me-2 label-min-width text-start">
                  {labelInfo.label}
                </span>
                <span className="me-2">:</span>
                <span className="text-break">
                  {getBankFieldValue
                    ? getBankFieldValue(employee, labelInfo.key)
                    : "N/A"}
                </span>
              </div>
            ))}
        </div>
      </div>

      {/* Earnings & Deductions */}
      <div className="d-flex flex-column flex-md-row gap-3 gap-md-4 mb-3 mb-md-4 pb-2 pb-md-3">
        {/* Earnings */}
        <div className="flex-grow-1 mb-3 mb-md-0">
          <h4 className="bg-light p-2 mb-2 mb-md-3 border-start border-primary border-3 fs-6">
            EARNINGS
          </h4>
          <table className="w-100 earnings-table">
            <tbody>
              {Object.entries(payslipLabels.earnings || {}).map(
                ([id, labelInfo]) => {
                  const displayValue = getCorrectEarningsValue(id, labelInfo);
                  return (
                    <tr key={id}>
                      <td className="pb-1 pb-md-2 d-flex align-items-center justify-content-start text-break">
                        {labelInfo.label}
                      </td>
                      <td className="text-end pb-1 pb-md-2">
                        {isEditing ? (
                          <input
                            type="text"
                            className="form-control form-control-sm text-end mobile-input"
                            value={
                              editableEarnings[id] !== undefined
                                ? editableEarnings[id]
                                : displayValue
                            }
                            onChange={(e) => {
                              handleEarningsChange(id, e.target.value);
                            }}
                            placeholder="0"
                          />
                        ) : (
                          `₹${(displayValue || 0).toLocaleString("en-IN")}`
                        )}
                      </td>
                    </tr>
                  );
                }
              )}
              <tr className="border-top">
                <td className="pt-1 pt-md-2 fw-bold d-flex align-items-center justify-content-start">
                  Gross Earnings
                </td>
                <td className="text-end pt-1 pt-md-2 fw-bold">
                  ₹{totalEarnings.toLocaleString("en-IN")}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Deductions */}
        <div className="flex-grow-1">
          <h4 className="bg-light p-2 mb-2 mb-md-3 border-start border-primary border-3 fs-6">
            DEDUCTIONS
          </h4>
          <table className="w-100 deductions-table">
            <tbody>
              {Object.entries(payslipLabels.deductions || {}).map(
                ([id, labelInfo]) => {
                  const displayValue = getCorrectDeductionsValue(id, labelInfo);
                  return (
                    <tr key={id}>
                      <td className="pb-1 pb-md-2 d-flex align-items-center justify-content-start text-break">
                        {labelInfo.label}
                      </td>
                      <td className="text-end pb-1 pb-md-2">
                        {isEditing ? (
                          <input
                            type="text"
                            className="form-control form-control-sm text-end mobile-input"
                            value={
                              editableDeductions[id] !== undefined
                                ? editableDeductions[id]
                                : displayValue
                            }
                            onChange={(e) => {
                              handleDeductionsChange(id, e.target.value);
                            }}
                            placeholder="0"
                          />
                        ) : (
                          `₹${(displayValue || 0).toLocaleString("en-IN")}`
                        )}
                      </td>
                    </tr>
                  );
                }
              )}
              <tr className="border-top">
                <td className="pt-1 pt-md-2 fw-bold">Total Deductions</td>
                <td className="text-end pt-1 pt-md-2 fw-bold">
                  ₹{totalDeductions.toLocaleString("en-IN")}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Net Pay Summary */}
      <div
        className="p-2 p-md-3 mb-3 mb-md-4 net-pay-amount"
        style={{ borderRadius: "10px", border: "1px solid #d3d3d3" }}
      >
        <div className="d-flex justify-content-between fs-6 fs-md-5 fw-bold mb-1 mb-md-2">
          <span className="text-break">Net Payable Amount</span>
          <span className="text-success ms-2">
            ₹{netPay.toLocaleString("en-IN")}
          </span>
        </div>
        <div className="fst-italic text-muted text-start fs-6">
          {numberToWords
            ? numberToWords(Math.round(netPay))
            : "Amount in words not available"}
        </div>
      </div>
    </div>
  );
};

export default PayslipTemplate;
